import { useEffect, useRef, useState, useCallback } from 'react';

import { saveDraft, clearDraft } from '../../components/digitalMarketing/draftStore';

// An unsent form kept in the browser, so a crash, a closed tab or a phone call does not cost
// someone a half-written invoice.
//
// Same idea, and the same IndexedDB store, as Digital Marketing's upload drafts
// (components/digitalMarketing/draftStore.js - read its header for why it is IndexedDB and why it
// is local): the draft is NOT a record. Nothing reaches the server until the person presses Save,
// so a draft never takes a document number, never shows to anyone else and never touches stock.
//
//   key        one draft per key ('mis:invoice:<branchId>') - a second one overwrites the first
//   kind       what a strip lists it under ('misInvoice', 'misQuote', ...)
//   enabled    true only while a NEW document's form is open (an edit has a saved record to go back to)
//   dirty      nothing is stored for a form nobody has touched
//   snapshot   the form's state - JSON-safe
//   meta       small fields a strip shows without opening the draft ({ branchId, title })
//
// Every call swallows its own errors (a private window has no IndexedDB): a form with no draft is
// still a working form.
export default function useFormDraft({ key, kind, enabled, dirty, snapshot, meta }) {
  const [savedAt, setSavedAt] = useState(null);
  const latest = useRef({});
  latest.current = { key, kind, snapshot, meta, enabled, dirty };
  // After a save or an explicit discard no pending timer may bring the draft back.
  const settled = useRef(false);
  const text = JSON.stringify(snapshot);

  useEffect(() => {
    if (enabled) settled.current = false;
    else setSavedAt(null);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !dirty) return undefined;
    const id = setTimeout(async () => {
      if (settled.current) return;
      const { key: k, kind: kd, snapshot: snap, meta: m } = latest.current;
      const ok = await saveDraft(k, { kind: kd, snapshot: snap, ...(m || {}) });
      if (ok && !settled.current) setSavedAt(Date.now());
    }, 800);
    return () => clearTimeout(id);
  }, [enabled, dirty, text, key]);

  /** Store the draft NOW - for a form being closed before the autosave timer fired. */
  const flush = useCallback(async () => {
    const { key: k, kind: kd, snapshot: snap, meta: m, enabled: en, dirty: dt } = latest.current;
    if (!en || !dt) return false;
    settled.current = false;
    return saveDraft(k, { kind: kd, snapshot: snap, ...(m || {}) });
  }, []);

  /** Forget the draft: after a successful save, or when the person discards it. */
  const clear = useCallback(async () => {
    settled.current = true;
    setSavedAt(null);
    await clearDraft(latest.current.key);
  }, []);

  return { savedAt, clear, flush };
}
